// use history from react-router-dom
// eslint-disable-next-line import/no-extraneous-dependencies
import { Text, TextSize } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { Article, ChevronRight } from '@wix/wix-ui-icons-common';
import { To } from 'history';
import React, { FC, memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';

import { SearchArticlesItem } from '../../../common/api/searchArticles';
import { helpCenterSlice } from '../../../common/reducers/helpCenter';
import { homepageService } from '../../../common/services/homepage/homepageService';
import { appRoutes } from '../../../react-router';
import { classNames } from '../../../util';
import { biHelpCenter } from '../../../util/bi';
import s from './styles.scss';

export type ArticlesListItemProps = {
  id: string;
  title: string;
  titleKey?: string;
  rowNumber: number;
  interactionLogger: (
    id: string,
    rowNumber: number,
    categoryId?: string,
  ) => void;
  categoryId?: string;
  textSize?: TextSize;
  smallPaddings?: boolean;
  withChevronRight?: boolean;
  ellipsis?: boolean;
  dataHook?: string;
};

export const getLinkPath = (
  articleId: string,
  searchParams: URLSearchParams,
): To => {
  searchParams.delete('query');
  return {
    pathname: appRoutes.articleById({ articleId }),
    search: searchParams.toString(),
  };
};

// TODO: Add support for other types of solutions
export const ArticlesListItem: FC<ArticlesListItemProps> = memo(
  function ArticlesListItemComponent({
    id,
    title,
    titleKey,
    rowNumber,
    interactionLogger,
    categoryId,
    textSize = homepageService.homepageEnabled ? 'medium' : 'small',
    smallPaddings = false,
    withChevronRight = false,
    ellipsis = false,
    dataHook = 'articles-list__item',
  }) {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();
    const handleArticleClick = useCallback(() => {
      dispatch(
        helpCenterSlice.actions.setCurrentArticle({ id, title, categoryId }),
      );
      interactionLogger(id, rowNumber, categoryId);
    }, [dispatch, id, title, categoryId, interactionLogger, rowNumber]);

    const className = classNames([
      s.articlesListItem,
      smallPaddings && s.articlesListItemSmall,
      rowNumber === 0 && s.articlesListItemFirstItem,
      withChevronRight && s.articlesListItemWithChevron,
      homepageService.homepageEnabled && s.articlesListItemWithHomepage,
    ]);

    return (
      <Link
        className={className}
        to={getLinkPath(id, searchParams)}
        onClick={handleArticleClick}
        data-hook={dataHook}
      >
        <Article className={s.articlesListItemIcon} size="24" fill="#3899EC" />
        <Text
          size={textSize}
          ellipsis={ellipsis}
          tooltipProps={{
            size: 'small',
            maxWidth: '80%',
          }}
          dataHook="articles-list-item"
        >
          {titleKey ? t(titleKey) : title}
        </Text>
        {withChevronRight && (
          <ChevronRight className={s.chevronRightIcon} size="24" />
        )}
      </Link>
    );
  },
);

export const renderArticlesListItem = (
  { id, title }: SearchArticlesItem,
  rowNumber: number,
) => (
  <ArticlesListItem
    id={id}
    title={title}
    rowNumber={rowNumber}
    interactionLogger={biHelpCenter.logSearchResultsInteraction.bind(
      biHelpCenter,
    )}
  />
);
